import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'

const EMPTY_LIST = []
const RESULT_PAGE_SIZE = 20

function trimOrEmpty(v) {
  return (v ?? '').toString().trim()
}

function formatDateTime(iso) {
  return formatApiDateTimeDisplay(undefined, iso)
}

function localizeGuidanceText(text) {
  const s = (text ?? '').toString()
  if (!s) return s
  const coverageKo = {
    cancer: '암',
    death: '사망',
    hospitalization: '입원',
    inpatient: '입원',
    surgery: '수술',
    inpatient_surgery: '입원/수술',
    stroke: '뇌혈관',
    brain: '뇌혈관',
    heart: '심장',
    ci2: '2대질환',
  }
  // [coverage=xxx] 형태를 '담보=한글'로 교체
  return s.replace(/\[coverage=([a-z0-9_]+)\]/ig, (_, code) => {
    const key = String(code || '').toLowerCase()
    const ko = coverageKo[key]
    return ko ? `[담보=${ko}]` : `[담보=${key}]`
  })
}

function formatMetaHint(meta) {
  const m = meta || {}
  const mode = m.mode
  const count = Number(m.expandedDiagnosisCodesCount || 0)
  const preview = Array.isArray(m.expandedDiagnosisCodesPreview) ? m.expandedDiagnosisCodesPreview : []
  if (mode === 'NAME_KCD') {
    const head = preview.length ? preview.join(', ') : ''
    if (count > 0) return `질병명 검색을 KCD 코드 ${count}개로 확장해 조회했습니다${head ? ` (예: ${head})` : ''}.`
    const avail = Boolean(m.kcdMasterAvailable)
    return avail
      ? '질병명으로 KCD 코드를 찾지 못해, 안내 문구 기준으로만 조회했습니다.'
      : '현재 KCD 마스터가 없어 질병명→코드 확장 검색이 제한됩니다. (안내 문구 기준으로 조회)'
  }
  if (mode === 'DX') return null
  return null
}

function KcdAutocomplete({ value, onChange, onPick, placeholder }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState(value || '')
  const [debounced, setDebounced] = useState('')
  const wrapRef = useRef(null)

  useEffect(() => {
    setQ(value || '')
  }, [value])

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 200)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => {
    function onDocDown(e) {
      const el = wrapRef.current
      if (el && !el.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [])

  const sugQuery = useQuery({
    queryKey: ['kcd-local-search', { q: debounced || '' }],
    enabled: Boolean((debounced || '').trim().length >= 2),
    queryFn: async () => {
      const res = await api.get('/kcd/local-search', { params: { q: (debounced || '').trim(), mode: 'name', size: 12 } })
      return res.data?.data || res.data
    },
  })

  const items = Array.isArray(sugQuery.data?.items) ? sugQuery.data.items : []
  const available = sugQuery.data?.meta?.available !== false

  return (
    <div ref={wrapRef} className="relative">
      <input
        className="mt-1 w-full rounded border px-3 py-2 text-sm"
        placeholder={placeholder}
        value={q}
        onChange={(e) => {
          const v = e.target.value
          setQ(v)
          onChange && onChange(v)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false)
        }}
      />
      {open && (q || '').trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg overflow-hidden">
          {!available && (
            <div className="px-3 py-2 text-xs text-slate-500">KCD 자동완성 데이터가 준비되지 않았습니다.</div>
          )}
          {available && sugQuery.isFetching && (
            <div className="px-3 py-2 text-xs text-slate-500">검색 중…</div>
          )}
          {available && !sugQuery.isFetching && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">추천 결과가 없습니다.</div>
          )}
          {available && items.length > 0 && (
            <div className="max-h-64 overflow-auto">
              {items.map((it, idx) => (
                <button
                  key={`${it?.code || 'code'}-${idx}`}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-slate-50"
                  onMouseDown={(e) => {
                    // blur로 dropdown 닫히기 전에 pick 처리
                    e.preventDefault()
                    onPick && onPick(it)
                    setOpen(false)
                  }}
                >
                  <div className="text-sm text-slate-800 font-medium">{it?.nameKo || '-'}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{it?.code || ''}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function buildConditionSummary(rule) {
  const parts = []
  const waitingDays = rule?.waitingDays
  const maxCount = rule?.maxCount
  const inpatientMaxDays = rule?.inpatientMaxDays

  if (typeof waitingDays === 'number' && waitingDays > 0) parts.push(`치료종료 후 ${waitingDays}일`)
  if (typeof maxCount === 'number' && maxCount > 0) parts.push(`${maxCount}회 이내`)
  if (typeof inpatientMaxDays === 'number' && inpatientMaxDays >= 0) parts.push(`입원 ${inpatientMaxDays}일 이내`)

  return parts.length ? parts.join(' · ') : '조건 정보 없음'
}

function formatCodesPreview(codes, limit = 3, suffixUnit = '개') {
  const arr = Array.isArray(codes) ? codes.filter(Boolean) : []
  if (arr.length === 0) return '-'
  const head = arr.slice(0, limit).join(', ')
  return arr.length > limit ? `${head} 외 ${arr.length - limit}${suffixUnit}` : head
}

function toNumberOrNull(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function formatInpatientDays(rule) {
  const days = toNumberOrNull(rule?.inpatientMaxDays)
  return days != null && days >= 0 ? `${days}일 이내` : '-'
}

function formatUnderwritingPeriod(rule) {
  const days = toNumberOrNull(rule?.waitingDays)
  return days != null && days > 0 ? `치료종료 후 ${days}일` : '-'
}

function formatSurgeryRequirement(rule) {
  const text = [
    rule?.conditionKey,
    rule?.coverageCode,
    rule?.message,
  ].filter(Boolean).join(' ')
  return /수술|시술|surgery|operation/i.test(text) ? '해당' : '-'
}

function formatDiagnosisCodesForRule(rule, fallbackCode, meta) {
  const enteredCode = trimOrEmpty(fallbackCode).toUpperCase()
  if (enteredCode) return enteredCode

  const codes = Array.isArray(rule?.diagnosisCodes) ? rule.diagnosisCodes : []
  const prefixes = Array.isArray(rule?.diagnosisPrefixes) ? rule.diagnosisPrefixes : []
  const ranges = Array.isArray(rule?.diagnosisRangesM3) ? rule.diagnosisRangesM3 : []
  const preview = Array.isArray(meta?.expandedDiagnosisCodesPreview) ? meta.expandedDiagnosisCodesPreview : []
  const parts = []
  if (codes.length) parts.push(formatCodesPreview(codes, 2))
  if (prefixes.length) parts.push(`${formatCodesPreview(prefixes, 2)} 시작`)
  if (ranges.length) parts.push(formatCodesPreview(ranges, 2))
  if (parts.length) return parts.join(' / ')
  return formatCodesPreview(preview, 2)
}

function formatDiseaseNameForRule(rule, fallbackName) {
  const enteredName = trimOrEmpty(fallbackName)
  if (enteredName) return enteredName

  const keywords = Array.isArray(rule?.nameKeywords) ? rule.nameKeywords : []
  if (keywords.length) return formatCodesPreview(keywords, 2)

  return localizeGuidanceText(rule?.message || '') || '-'
}

function detectInsurerKind(source) {
  const text = [
    source?.insurerKind,
    source?.kind,
    source?.businessType,
    source?.insurerType,
    source?.category,
    source?.code,
    source?.displayName,
    source?.name,
    source?.insurerName,
  ].filter(Boolean).join(' ').toLowerCase()
  if (/손해|화재|해상|손보|non[_\s-]?life|property|casualty|fire|marine/.test(text)) return 'nonLife'
  if (/생명|생보|\blife\b/.test(text)) return 'life'
  return 'unknown'
}

function ConditionBadge({ value, tone = 'slate' }) {
  if (!value || value === '-') return <span className="text-slate-400">-</span>
  const toneClass = {
    sky: 'border-sky-200 bg-sky-50 text-sky-700',
    teal: 'border-teal-200 bg-teal-50 text-teal-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  }[tone] || 'border-slate-200 bg-slate-50 text-slate-700'
  return (
    <span className={`inline-flex min-w-[72px] justify-center rounded-md border px-2.5 py-1 text-xs font-semibold ${toneClass}`}>
      {value}
    </span>
  )
}

export default function Uw3nnExceptionsLookupPage() {
  const [dxCodeDraft, setDxCodeDraft] = useState('')
  const [dxNameDraft, setDxNameDraft] = useState('')
  const [dxCode, setDxCode] = useState('')
  const [dxName, setDxName] = useState('')
  const [selectedInsurerCode, setSelectedInsurerCode] = useState('')
  const [insurerKindFilter, setInsurerKindFilter] = useState('all')
  const [resultPage, setResultPage] = useState(0)
  const [insDxCode, setInsDxCode] = useState('')
  const [insDxName, setInsDxName] = useState('')

  const searchQuery = useQuery({
    queryKey: ['uw3nn-exceptions-search', {
      diagnosisCode: dxCode || '',
      diagnosisName: dxName || '',
      insurerKind: insurerKindFilter,
      page: resultPage,
      size: RESULT_PAGE_SIZE,
    }],
    enabled: Boolean(!trimOrEmpty(selectedInsurerCode) && (trimOrEmpty(dxCode) || trimOrEmpty(dxName))),
    queryFn: async () => {
      const res = await api.get('/underwriting/3nn/exceptions/search', {
        params: {
          diagnosisCode: trimOrEmpty(dxCode) || undefined,
          diagnosisName: trimOrEmpty(dxName) || undefined,
          insurerKind: insurerKindFilter !== 'all' ? (insurerKindFilter === 'nonLife' ? 'NON_LIFE' : 'LIFE') : undefined,
          page: resultPage,
          size: RESULT_PAGE_SIZE,
        },
      })
      return res.data?.data || res.data
    },
  })

  const searchItems = useMemo(() => searchQuery.data?.items ?? EMPTY_LIST, [searchQuery.data])
  const searchMeta = searchQuery.data?.meta
  const searchMetaHint = useMemo(() => formatMetaHint(searchMeta), [searchMeta])

  const insurersQuery = useQuery({
    queryKey: ['insurers', { scope: 'active' }],
    queryFn: async () => {
      const res = await api.get('/insurers')
      return res.data?.data || res.data
    },
  })
  const insurers = useMemo(() => insurersQuery.data?.items ?? EMPTY_LIST, [insurersQuery.data])

  const insurerOptions = useMemo(() => {
    const arr = Array.isArray(insurers) ? insurers : []
    return arr
      .map((x) => ({
        code: x?.code,
        displayName: x?.displayName || x?.name || x?.code,
        kind: detectInsurerKind(x),
      }))
      .filter((x) => x.code && x.displayName)
      .sort((a, b) => String(a.displayName).localeCompare(String(b.displayName)))
  }, [insurers])

  const insurerLookup = useMemo(() => {
    const byCode = new Map()
    const byName = new Map()
    for (const insurer of insurerOptions) {
      byCode.set(insurer.code, insurer)
      byName.set(insurer.displayName, insurer)
    }
    return { byCode, byName }
  }, [insurerOptions])

  const selectedInsurer = useMemo(() => {
    return insurerOptions.find((x) => x.code === selectedInsurerCode) || null
  }, [insurerOptions, selectedInsurerCode])

  const selectedInsurerLabel = selectedInsurer?.displayName || ''
  const selectedInsurerKind = selectedInsurer?.kind || 'unknown'

  const byInsurerQuery = useQuery({
    queryKey: ['uw3nn-exceptions-by-insurer', {
      insurerCode: selectedInsurerCode || '',
      diagnosisCode: insDxCode || '',
      diagnosisName: insDxName || '',
      page: resultPage,
      size: RESULT_PAGE_SIZE,
    }],
    enabled: Boolean(trimOrEmpty(selectedInsurerCode)),
    queryFn: async () => {
      const res = await api.get('/underwriting/3nn/exceptions/by-insurer', {
        params: {
          insurerCode: selectedInsurerCode,
          diagnosisCode: trimOrEmpty(insDxCode) || undefined,
          diagnosisName: trimOrEmpty(insDxName) || undefined,
          page: resultPage,
          size: RESULT_PAGE_SIZE,
        },
      })
      return res.data?.data || res.data
    },
  })

  const byInsurerItems = useMemo(() => byInsurerQuery.data?.items ?? EMPTY_LIST, [byInsurerQuery.data])

  const catalogSummaryQuery = useQuery({
    queryKey: ['uw3nn-exceptions-summary'],
    queryFn: async () => {
      const res = await api.get('/underwriting/3nn/exceptions/summary')
      return res.data?.data || res.data
    },
    staleTime: 5 * 60 * 1000,
  })

  const catalogSummary = catalogSummaryQuery.data || {}
  const catalogInsurerCount = Number(catalogSummary.insurerCount || 0) || insurerOptions.length
  const catalogExceptionCount = Number(catalogSummary.exceptionItemCount || catalogSummary.exceptionRuleCount || 0)
  const catalogIntro = catalogInsurerCount > 0 && catalogExceptionCount > 0
    ? `현재 ${catalogInsurerCount.toLocaleString()}개 보험사의 ${catalogExceptionCount.toLocaleString()}개 예외질환 항목을 조회할 수 있으며, 데이터는 계속 업데이트 중입니다.`
    : catalogInsurerCount > 0
      ? `현재 ${catalogInsurerCount.toLocaleString()}개 보험사의 예외질환 항목을 조회할 수 있으며, 데이터는 계속 업데이트 중입니다.`
      : '보험사별 예외질환 항목을 조회할 수 있으며, 데이터는 계속 업데이트 중입니다.'

  const runSearch = () => {
    const nextCode = trimOrEmpty(dxCodeDraft).toUpperCase()
    const nextName = trimOrEmpty(dxNameDraft)
    setDxCode(nextCode)
    setDxName(nextName)
    setInsDxCode(nextCode)
    setInsDxName(nextName)
    setResultPage(0)
  }

  const resetSearch = () => {
    setDxCodeDraft('')
    setDxNameDraft('')
    setDxCode('')
    setDxName('')
    setSelectedInsurerCode('')
    setInsDxCode('')
    setInsDxName('')
    setInsurerKindFilter('all')
    setResultPage(0)
  }

  const searchRows = useMemo(() => {
    const fallbackCode = trimOrEmpty(dxCode)
    const fallbackName = trimOrEmpty(dxName)
    return (Array.isArray(searchItems) ? searchItems : []).map((item, idx) => {
      const insurerInfo = insurerLookup.byCode.get(item?.insurerCode) || insurerLookup.byName.get(item?.insurerName)
      const insurerName = item?.insurerName || insurerInfo?.displayName || '보험사'
      return {
        id: `search-${item?.insurerCode || insurerName}-${idx}`,
        insurerName,
        insurerKind: insurerInfo?.kind || detectInsurerKind(item),
        diseaseCode: formatDiagnosisCodesForRule(item, fallbackCode, searchMeta),
        diseaseName: formatDiseaseNameForRule(item, fallbackName),
        inpatientDays: formatInpatientDays(item),
        surgeryRequirement: formatSurgeryRequirement(item),
        underwritingPeriod: formatUnderwritingPeriod(item),
        conditionSummary: buildConditionSummary(item),
        guidance: localizeGuidanceText(item?.message || ''),
        updatedAtIso: item?.updatedAt || null,
      }
    }).sort((a, b) => {
      const insurerCompare = String(a.insurerName).localeCompare(String(b.insurerName))
      if (insurerCompare !== 0) return insurerCompare
      return String(a.diseaseName).localeCompare(String(b.diseaseName))
    })
  }, [dxCode, dxName, insurerLookup, searchItems, searchMeta])

  const byInsurerRows = useMemo(() => {
    const fallbackCode = trimOrEmpty(insDxCode)
    const fallbackName = trimOrEmpty(insDxName)
    return (Array.isArray(byInsurerItems) ? byInsurerItems : []).map((item, idx) => ({
      id: `insurer-${selectedInsurerCode}-${item?.ruleId || idx}`,
      insurerName: selectedInsurerLabel || '보험사',
      insurerKind: selectedInsurerKind,
      diseaseCode: formatDiagnosisCodesForRule(item, fallbackCode),
      diseaseName: formatDiseaseNameForRule(item, fallbackName),
      inpatientDays: formatInpatientDays(item),
      surgeryRequirement: formatSurgeryRequirement(item),
      underwritingPeriod: formatUnderwritingPeriod(item),
      conditionSummary: buildConditionSummary(item),
      guidance: localizeGuidanceText(item?.message || ''),
      updatedAtIso: null,
    }))
  }, [byInsurerItems, insDxCode, insDxName, selectedInsurerCode, selectedInsurerKind, selectedInsurerLabel])

  const baseRows = selectedInsurerCode ? byInsurerRows : searchRows
  const filteredRows = useMemo(() => {
    if (insurerKindFilter === 'all') return baseRows
    return baseRows.filter((row) => row.insurerKind === insurerKindFilter)
  }, [baseRows, insurerKindFilter])

  const activeQuery = selectedInsurerCode ? byInsurerQuery : searchQuery
  const activeData = selectedInsurerCode ? byInsurerQuery.data : searchQuery.data
  const hasSearchCondition = Boolean(trimOrEmpty(selectedInsurerCode) || trimOrEmpty(dxCode) || trimOrEmpty(dxName))
  const isLoading = hasSearchCondition && activeQuery.isFetching
  const hasError = hasSearchCondition && activeQuery.isError
  const totalElements = Number(activeData?.totalElements ?? filteredRows.length ?? 0)
  const totalPages = Math.max(0, Number(activeData?.totalPages ?? 0))
  const displayPage = Math.max(0, Number(activeData?.page ?? resultPage))
  const latestUpdatedAtIso = useMemo(() => {
    let latest = null
    for (const row of filteredRows) {
      if (!row.updatedAtIso) continue
      const t = new Date(row.updatedAtIso).getTime()
      if (!Number.isNaN(t) && (latest == null || t > latest)) latest = t
    }
    return latest != null ? new Date(latest).toISOString() : null
  }, [filteredRows])

  const kindFilters = [
    { value: 'all', label: '전체' },
    { value: 'nonLife', label: '손보' },
    { value: 'life', label: '생보' },
  ]
  const activePageSize = Math.max(1, Number(activeData?.size || RESULT_PAGE_SIZE))
  const resultStart = totalElements > 0 ? (displayPage * activePageSize) + 1 : 0
  const resultEnd = totalElements > 0 ? Math.min(totalElements, resultStart + filteredRows.length - 1) : 0
  const canGoPrev = hasSearchCondition && displayPage > 0
  const canGoNext = hasSearchCondition && totalPages > 0 && displayPage + 1 < totalPages

  const resultMetaText = isLoading
    ? '조회 중'
    : `총 ${totalElements.toLocaleString()}건${latestUpdatedAtIso ? ` · 데이터 기준 ${formatDateTime(latestUpdatedAtIso)}` : ''}`

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="text-2xl font-semibold text-slate-900">예외질환 조회</div>
        <MenuGuideButton slug="uw3nn-exceptions" title="설계지원 DB 가이드" />
      </div>

      <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950">
        <div className="font-semibold">예외질환 데이터가 계속 확장되고 있습니다.</div>
        <div className="mt-1 text-xs text-sky-800">{catalogIntro}</div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(260px,1.4fr)_180px_240px_auto_auto] lg:items-end">
          <div>
            <label className="text-xs font-semibold text-slate-500">질병명</label>
            <KcdAutocomplete
              value={dxNameDraft}
              placeholder="예: 당뇨, 고혈압, C50"
              onChange={(v) => setDxNameDraft(v)}
              onPick={(it) => {
                if (it?.code) setDxCodeDraft(String(it.code))
                if (it?.nameKo) setDxNameDraft(String(it.nameKo))
              }}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">질병코드</label>
            <input
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
              placeholder="예: K291"
              value={dxCodeDraft}
              onChange={(e) => setDxCodeDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch()
              }}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">보험사</label>
            <select
              className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
              value={selectedInsurerCode}
              onChange={(e) => {
                setSelectedInsurerCode(e.target.value)
                setInsurerKindFilter('all')
                setResultPage(0)
              }}
            >
              <option value="">전체 ({insurerOptions.length.toLocaleString()}개)</option>
              {insurerOptions.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.displayName}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="rounded bg-brand-sky px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            disabled={!trimOrEmpty(selectedInsurerCode) && !trimOrEmpty(dxCodeDraft) && !trimOrEmpty(dxNameDraft)}
            onClick={runSearch}
          >
            검색
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            onClick={resetSearch}
          >
            초기화
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500">구분</span>
          {kindFilters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              aria-pressed={insurerKindFilter === filter.value}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${
                insurerKindFilter === filter.value
                  ? 'border-brand-sky bg-brand-sky text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50'
              }`}
              disabled={Boolean(selectedInsurerCode)}
              onClick={() => {
                setInsurerKindFilter(filter.value)
                setResultPage(0)
              }}
            >
              {filter.label}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-500">{resultMetaText}</span>
        </div>

        {searchMetaHint && !selectedInsurerCode && (
          <div className="mt-3 text-xs text-slate-500">
            {searchMetaHint}
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-sm font-semibold text-slate-900">조회 결과</div>
          <div className="text-xs text-slate-500">{selectedInsurerLabel || '전체 보험사'}</div>
        </div>

        <div className="overflow-auto">
          <table className="min-w-[920px] w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500">
                <th className="px-4 py-3">보험사</th>
                <th className="px-4 py-3">질병코드</th>
                <th className="px-4 py-3">질병명</th>
                <th className="px-4 py-3 text-center">입원일수</th>
                <th className="px-4 py-3 text-center">수술여부</th>
                <th className="px-4 py-3 text-center">인수기간</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">조회 중입니다.</td>
                </tr>
              )}
              {!isLoading && hasError && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-red-600">조회 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.</td>
                </tr>
              )}
              {!isLoading && !hasError && !hasSearchCondition && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">보험사 또는 질병 정보를 입력해 주세요.</td>
                </tr>
              )}
              {!isLoading && !hasError && hasSearchCondition && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">검색 결과가 없습니다.</td>
                </tr>
              )}
              {!isLoading && !hasError && filteredRows.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70" title={row.guidance || row.conditionSummary}>
                  <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{row.insurerName}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      {row.diseaseCode}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-800">
                    <div className="max-w-[360px] truncate font-medium">{row.diseaseName}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ConditionBadge value={row.inpatientDays} tone="sky" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ConditionBadge value={row.surgeryRequirement} tone="teal" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ConditionBadge value={row.underwritingPeriod} tone="amber" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasSearchCondition && !hasError && totalElements > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3">
            <div className="text-xs text-slate-500">
              {resultStart.toLocaleString()}-{resultEnd.toLocaleString()} / {totalElements.toLocaleString()}건
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!canGoPrev || isLoading}
                onClick={() => setResultPage((prev) => Math.max(0, prev - 1))}
              >
                이전
              </button>
              <span className="min-w-[72px] text-center text-xs text-slate-500">
                {displayPage + 1} / {Math.max(totalPages, 1)}
              </span>
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!canGoNext || isLoading}
                onClick={() => setResultPage((prev) => prev + 1)}
              >
                다음
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
